import { useAddContentModal } from '@/features/events/components/AddContent';
import { useCustomizeMode } from '@/features/experiences/components/CustomizeMode';
import { usePageExperienceContext } from '@/features/experiences/contexts/PageExperienceContext';
import { MagicCtaButton } from '@/features/magic/components/MagicCtaButton';
import { printVisibility } from '@/features/styles/lib/printStyles';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useIsMobileBreakpoint } from '@/hooks/useIsBreakpoint';

/**
 * The planner's add-content CTA on mobile, where it's dropped from the
 * (crowded) PlatformBar. A floating pill in the bottom corner rather than a
 * full-width fixed bar: one action doesn't earn a bar, and floating leaves the
 * planner's own bottom content visible behind it.
 *
 * Mirrors MagicButton's visibility guards (readOnly / plannerAddContentEnabled
 * / magic mode / customize mode) since it doesn't render that component.
 */
export const PlannerMobileAddButton = () => {
  const isMobile = useIsMobileBreakpoint();
  const { readOnly } = usePageExperienceContext();
  const { value: plannerAddContentEnabled } = useFeatureFlag(
    'plannerAddContentEnabled'
  );
  const { isMagicMode, isCustomizeModeActive } = useCustomizeMode();
  const { open } = useAddContentModal();

  const canShowMagic =
    plannerAddContentEnabled && !isMagicMode && !isCustomizeModeActive;

  if (!isMobile || readOnly || !canShowMagic) return null;

  return (
    <MagicCtaButton
      borderRadius="ah-pill"
      bottom="calc(env(safe-area-inset-bottom) + 16px)"
      boxShadow="0 10px 28px -8px rgba(0, 0, 0, 0.5)"
      h="44px"
      // Centered, not in a corner: the bottom right belongs to the Intercom
      // launcher. Auto margins rather than a translate, which would shift this
      // odd-width pill by a half pixel and soften the label.
      insetX={0}
      mx="auto"
      position="fixed"
      prominent
      // Taller and wider than the `ah` bar sizing: this is a standalone touch
      // target rather than one control in a row.
      px={5}
      size="ah"
      sx={printVisibility.hide()}
      w="fit-content"
      zIndex={15}
      onClick={() => open()}
    >
      Add
    </MagicCtaButton>
  );
};
